// /branches — manage Dolt branches: list, create, switch (re-issue JWT),
// view commit log, diff against another branch, merge, delete.
//
// Single-file by intent: the feature surface is small. If sub-panels grow,
// split into components/branches/*.

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import {
  listBranches,
  createBranch,
  deleteBranch,
  getBranchLog,
  getBranchDiff,
  getCommitDiff,
  getCommitTableDiff,
  mergeBranch,
} from "../services/branches";
import {
  GitBranch,
  GitMerge,
  Plus,
  Trash2,
  CheckCircle2,
  ArrowLeftRight,
  History,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

export default function Branches() {
  const { user, switchBranch } = useAuth();
  const { branch: activeBranch } = useProject();

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-row UI state
  const [selectedForLog, setSelectedForLog] = useState(null);
  const [selectedForDiff, setSelectedForDiff] = useState(null);
  const [selectedForMerge, setSelectedForMerge] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const list = await listBranches();
      setBranches(list);
    } catch (e) {
      setError(e?.message || "Failed to load branches");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleSwitch = async (name) => {
    // switchBranch navigates to /p/<slug>/b/<name>/... (URL is the source of
    // truth); every page-level fetch reissues against the new branch on load.
    // Do NOT reload here — it would cancel the navigation.
    const res = await switchBranch(name);
    if (!res.success) alert(`Switch failed: ${res.error}`);
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
      {/* Title + Action */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Branches</h1>
          <p className="text-sm font-normal text-page-muted">
            Version the data dictionary. Each branch is an isolated workspace; merging brings changes back to <code>main</code>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <span className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] bg-card-bg text-sm text-page-text">
            <GitBranch size={16} />
            Active: <strong className="font-semibold">{activeBranch}</strong>
          </span>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <Plus size={20} /> New Branch
          </button>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm">{error}</div>
      )}

      {/* Branch list */}
      <div className="bg-card-bg rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Latest commit</th>
              <th className="px-5 py-3 font-medium">By</th>
              <th className="px-5 py-3 font-medium w-[180px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-page-muted">
                <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
              </td></tr>
            ) : branches.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-page-muted">No branches.</td></tr>
            ) : branches.map(b => {
              const isActive = b.name === activeBranch;
              const isMain = b.name === "main";
              return (
                <tr key={b.name} className="border-b border-page-divider last:border-b-0 hover:bg-page-bg/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <GitBranch size={16} className="shrink-0 text-page-muted" />
                      <span className="font-medium">{b.name}</span>
                      {isActive && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                          <CheckCircle2 size={12} /> active
                        </span>
                      )}
                      {isMain && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">main</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-page-muted truncate max-w-[400px]">
                    {b.latest_commit_message || "—"}
                  </td>
                  <td className="px-5 py-3 text-page-muted">{b.latest_committer || "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {!isActive && (
                        <button
                          onClick={() => handleSwitch(b.name)}
                          title="Switch to this branch"
                          className="px-2 py-1 rounded text-xs hover:bg-page-bg"
                        >Switch</button>
                      )}
                      <button
                        onClick={() => setSelectedForLog(b.name)}
                        title="Commit log"
                        className="p-1.5 rounded hover:bg-page-bg"
                      ><History size={16} /></button>
                      <button
                        onClick={() => setSelectedForDiff(b.name)}
                        title="Diff against main"
                        className="p-1.5 rounded hover:bg-page-bg"
                      ><ArrowLeftRight size={16} /></button>
                      {!isMain && (
                        <button
                          onClick={() => setSelectedForMerge(b.name)}
                          title="Merge into main"
                          className="p-1.5 rounded hover:bg-page-bg"
                        ><GitMerge size={16} /></button>
                      )}
                      {!isMain && !isActive && (
                        <button
                          onClick={() => setSelectedForDelete(b.name)}
                          title="Delete branch"
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        ><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateBranchModal
          parents={branches.map(b => b.name)}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); }}
        />
      )}

      {selectedForLog && (
        <LogPanel
          branch={selectedForLog}
          onClose={() => setSelectedForLog(null)}
        />
      )}

      {selectedForDiff && (
        <DiffPanel
          branch={selectedForDiff}
          allBranches={branches.map(b => b.name)}
          onClose={() => setSelectedForDiff(null)}
        />
      )}

      {selectedForMerge && (
        <MergeDialog
          branch={selectedForMerge}
          onClose={() => setSelectedForMerge(null)}
          onSuccess={() => { setSelectedForMerge(null); load(); }}
        />
      )}

      {selectedForDelete && (
        <DeleteDialog
          branch={selectedForDelete}
          onClose={() => setSelectedForDelete(null)}
          onSuccess={() => { setSelectedForDelete(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Modal primitive ────────────────────────────────────────────────────
function ModalShell({ title, onClose, children, footer, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className={`bg-card-bg rounded-[8px] shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-md"} mt-12 md:mt-0`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Create ─────────────────────────────────────────────────────────────
function CreateBranchModal({ parents, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState(parents.includes("main") ? "main" : (parents[0] || "main"));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const valid = useMemo(() => /^[a-zA-Z0-9_-][a-zA-Z0-9_/-]{0,127}$/.test(name), [name]);

  const submit = async () => {
    if (!valid) { setErr("Invalid branch name. Letters, digits, _ - / only."); return; }
    setSubmitting(true); setErr(null);
    try {
      await createBranch({ name, from });
      onSuccess();
    } catch (e) {
      setErr(e?.message || "Failed to create branch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Create branch"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
          <button
            disabled={!valid || submitting}
            onClick={submit}
            className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Name</span>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. cleanup-quickbooks-mappings"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"
          />
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Branch from</span>
          <select
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"
          >
            {parents.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        {err && <div className="text-sm text-red-700">{err}</div>}
      </div>
    </ModalShell>
  );
}

// ─── Log panel (3-view state machine) ──────────────────────────────────
// View states:
//   { type: 'list' }                          — show all commits on the branch
//   { type: 'commit', commit }                — show table-level diff for a commit
//   { type: 'table', commit, table }          — show row-level diff for a table in that commit
function LogPanel({ branch, onClose }) {
  const [view, setView] = useState({ type: 'list' });
  const title =
    view.type === 'list' ? `Commits on ${branch}` :
    view.type === 'commit' ? `Commit ${view.commit.commit_hash.slice(0,8)}` :
    `${view.table} · ${view.commit.commit_hash.slice(0,8)}`;

  const back = () => {
    if (view.type === 'table') setView({ type: 'commit', commit: view.commit });
    else if (view.type === 'commit') setView({ type: 'list' });
  };

  return (
    <ModalShell title={title} onClose={onClose} wide
      footer={
        <div className="flex items-center gap-2 w-full">
          {view.type !== 'list' && (
            <button onClick={back} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm hover:bg-page-bg">
              <ChevronLeft size={16} /> Back
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Close</button>
        </div>
      }
    >
      {view.type === 'list' && (
        <LogList branch={branch} onSelect={c => setView({ type: 'commit', commit: c })} />
      )}
      {view.type === 'commit' && (
        <CommitDiffView
          branch={branch}
          commit={view.commit}
          onTable={(table) => setView({ type: 'table', commit: view.commit, table })}
        />
      )}
      {view.type === 'table' && (
        <TableDiffView
          branch={branch}
          commit={view.commit}
          table={view.table}
        />
      )}
    </ModalShell>
  );
}

function LogList({ branch, onSelect }) {
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    getBranchLog(branch, 100)
      .then(c => alive && setCommits(c))
      .catch(e => alive && setErr(e?.message || "Failed to load log"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [branch]);

  if (loading) return <div className="text-page-muted"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (commits.length === 0) return <div className="text-page-muted text-sm">No commits.</div>;
  return (
    <ul className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
      {commits.map(c => (
        <li key={c.commit_hash}>
          <button
            onClick={() => onSelect(c)}
            className="w-full text-left border-l-2 border-nav-active pl-3 py-1 hover:bg-page-bg/50 transition-colors flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs text-page-muted">{c.commit_hash.slice(0, 10)}</div>
              <div className="text-sm truncate">{c.message}</div>
              <div className="text-xs text-page-muted mt-0.5">
                {c.committer}{c.email ? ` <${c.email}>` : ""} · {fmtDate(c.date)}
              </div>
            </div>
            <ChevronRight size={16} className="text-page-muted shrink-0" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function CommitDiffView({ branch, commit, onTable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    getCommitDiff(branch, commit.commit_hash)
      .then(d => alive && setData(d))
      .catch(e => alive && setErr(e?.message || "Failed to load diff"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [branch, commit.commit_hash]);

  if (loading) return <div className="text-page-muted"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading diff…</div>;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm border border-page-divider rounded p-3 bg-page-bg/40">
        <div className="font-medium mb-1">{commit.message}</div>
        <div className="font-mono text-xs text-page-muted">{commit.commit_hash}</div>
        <div className="text-xs text-page-muted mt-1">
          {commit.committer}{commit.email ? ` <${commit.email}>` : ""} · {fmtDate(commit.date)}
        </div>
        {data?.parent && (
          <div className="text-xs text-page-muted mt-1">
            parent: <span className="font-mono">{data.parent.slice(0, 10)}</span>
          </div>
        )}
        {!data?.parent && (
          <div className="text-xs text-amber-700 mt-1">Root commit — no parent to diff against.</div>
        )}
      </div>
      {data?.tables?.length === 0 ? (
        <div className="text-page-muted text-sm">No table-level differences.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="py-2 font-medium">Table</th>
              <th className="py-2 font-medium">Change</th>
              <th className="py-2 font-medium">Schema</th>
              <th className="py-2 font-medium">Data</th>
              <th className="py-2 font-medium w-[100px]"></th>
            </tr>
          </thead>
          <tbody>
            {data?.tables?.map((t, i) => {
              const tbl = t.to_table_name || t.from_table_name;
              return (
                <tr key={i} className="border-b border-page-divider last:border-b-0 hover:bg-page-bg/40">
                  <td className="py-2">{tbl}</td>
                  <td className="py-2 capitalize">{t.diff_type}</td>
                  <td className="py-2">{t.schema_change ? "✓" : "—"}</td>
                  <td className="py-2">{t.data_change ? "✓" : "—"}</td>
                  <td className="py-2 text-right">
                    {tbl && (
                      <button
                        onClick={() => onTable(tbl)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-page-bg text-nav-active"
                      >
                        View rows <ChevronRight size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TableDiffView({ branch, commit, table }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    getCommitTableDiff(branch, commit.commit_hash, table, 100)
      .then(d => alive && setData(d))
      .catch(e => alive && setErr(e?.message || "Failed to load row diff"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [branch, commit.commit_hash, table]);

  if (loading) return <div className="text-page-muted"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading row diff…</div>;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (!data?.rows?.length) return <div className="text-page-muted text-sm">No row-level differences (the change may be schema-only).</div>;

  // Build the union of column names (without the from_/to_ prefix).
  const knownPrefixes = ['from_', 'to_'];
  const ignore = new Set(['from_commit', 'from_commit_date', 'to_commit', 'to_commit_date', 'diff_type']);
  const cols = new Set();
  for (const r of data.rows) {
    for (const k of Object.keys(r)) {
      if (ignore.has(k)) continue;
      for (const p of knownPrefixes) {
        if (k.startsWith(p)) { cols.add(k.slice(p.length)); break; }
      }
    }
  }
  const colList = [...cols];
  const stringify = (v) => v === null || v === undefined ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v);

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-page-muted">
        Showing up to 100 changed rows in <strong>{table}</strong>.
      </div>
      <div className="max-h-[60vh] overflow-auto">
        {data.rows.map((r, i) => (
          <div key={i} className={`border rounded mb-2 overflow-hidden text-sm ${
            r.diff_type === 'added' ? 'border-green-300' :
            r.diff_type === 'removed' ? 'border-red-300' :
            'border-amber-300'
          }`}>
            <div className={`px-3 py-1 text-xs font-medium capitalize ${
              r.diff_type === 'added' ? 'bg-green-50 text-green-800' :
              r.diff_type === 'removed' ? 'bg-red-50 text-red-800' :
              'bg-amber-50 text-amber-800'
            }`}>
              {r.diff_type} · id {r.to_id ?? r.from_id ?? '?'}
            </div>
            <table className="w-full">
              <tbody>
                {colList.map(col => {
                  const fromV = r[`from_${col}`];
                  const toV = r[`to_${col}`];
                  const changed = JSON.stringify(fromV) !== JSON.stringify(toV);
                  if (!changed && r.diff_type === 'modified') return null;
                  return (
                    <tr key={col} className={`border-t border-page-divider ${changed ? '' : 'opacity-60'}`}>
                      <td className="px-3 py-1 text-xs font-medium text-page-muted w-[180px] align-top">{col}</td>
                      <td className="px-3 py-1 text-xs align-top text-red-700 max-w-[40%] break-all">{stringify(fromV)}</td>
                      <td className="px-3 py-1 text-xs align-top text-page-muted shrink">→</td>
                      <td className="px-3 py-1 text-xs align-top text-green-700 max-w-[40%] break-all">{stringify(toV)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Diff panel ─────────────────────────────────────────────────────────
function DiffPanel({ branch, allBranches, onClose }) {
  const others = allBranches.filter(b => b !== branch);
  const [against, setAgainst] = useState(others.includes("main") ? "main" : (others[0] || "main"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (target) => {
    setLoading(true); setErr(null);
    try {
      setData(await getBranchDiff(branch, target));
    } catch (e) {
      setErr(e?.message || "Failed to compute diff");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(against); }, [against, branch]); // eslint-disable-line

  return (
    <ModalShell title={`Diff: ${branch}`} onClose={onClose} wide
      footer={<button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Close</button>}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-page-muted">Compare against:</span>
        <select
          value={against}
          onChange={e => setAgainst(e.target.value)}
          className="h-8 px-2 rounded border border-page-divider bg-page-bg text-sm"
        >
          {others.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-page-muted"><Loader2 className="inline animate-spin mr-2" size={16} /> Computing diff…</div>
      ) : err ? (
        <div className="text-red-700 text-sm">{err}</div>
      ) : !data?.tables?.length ? (
        <div className="text-page-muted text-sm">No table-level differences.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="py-2 font-medium">Table</th>
              <th className="py-2 font-medium">Change type</th>
              <th className="py-2 font-medium">Schema</th>
              <th className="py-2 font-medium">Data</th>
            </tr>
          </thead>
          <tbody>
            {data.tables.map((t, i) => (
              <tr key={i} className="border-b border-page-divider last:border-b-0">
                <td className="py-2">{t.to_table_name || t.from_table_name}</td>
                <td className="py-2 capitalize">{t.diff_type}</td>
                <td className="py-2">{t.schema_change ? "✓" : "—"}</td>
                <td className="py-2">{t.data_change ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModalShell>
  );
}

// ─── Merge dialog ───────────────────────────────────────────────────────
function MergeDialog({ branch, onClose, onSuccess }) {
  const [into, setInto] = useState("main");
  const [message, setMessage] = useState(`Merge branch '${branch}' into 'main'`);
  const [noFf, setNoFf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      const res = await mergeBranch(branch, { into, message, noFastForward: noFf });
      setResult(res);
    } catch (e) {
      setErr(e?.message || "Merge failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Merge ${branch}`} onClose={onClose}
      footer={
        result?.merged ? (
          <button onClick={() => { onSuccess(); }} className="px-3 py-1.5 rounded bg-nav-active text-white text-sm">Done</button>
        ) : (
          <>
            <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
            <button
              disabled={submitting}
              onClick={submit}
              className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50"
            >
              {submitting ? "Merging…" : "Merge"}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-2 text-sm">
          <div className="text-green-700">✓ {result.message || "Merge complete"}</div>
          <div className="text-page-muted">Fast forward: {result.fast_forward ? "yes" : "no"} · Conflicts: {result.conflicts ?? 0}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="text-sm">
            <span className="block text-page-muted mb-1">Into</span>
            <input
              value={into}
              onChange={e => setInto(e.target.value)}
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none"
            />
          </label>
          <label className="text-sm">
            <span className="block text-page-muted mb-1">Commit message</span>
            <input
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none"
            />
          </label>
          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={noFf} onChange={e => setNoFf(e.target.checked)} />
            <span>Force a merge commit (--no-ff)</span>
          </label>
          {err && <div className="text-sm text-red-700">{err}</div>}
        </div>
      )}
    </ModalShell>
  );
}

// ─── Delete confirm ─────────────────────────────────────────────────────
function DeleteDialog({ branch, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      await deleteBranch(branch);
      onSuccess();
    } catch (e) {
      setErr(e?.message || "Delete failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <ModalShell title={`Delete ${branch}`} onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
          <button
            disabled={submitting}
            onClick={submit}
            className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Deleting…" : "Delete"}
          </button>
        </>
      }
    >
      <p className="text-sm">
        Permanently delete branch <strong>{branch}</strong>?
        This cannot be undone unless the branch has been merged elsewhere.
      </p>
      {err && <div className="text-sm text-red-700 mt-3">{err}</div>}
    </ModalShell>
  );
}
